
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import Card from '@/core/components/Tela/Card.vue';
import SelecionarSazonalidade from '@/core/components/Tela/SelecionarSazonalidade.vue';
import SelecionarTipoProducao from '@/components/Cadastros/PCP/SelecionarTipoProducao.vue';
import SelecionarCategoriaPlanoConta from '@/components/Cadastros/PlanosContas/SelecionarCategoriaPlanoConta.vue';
import ServicoCipEstrutura from '@/servicos/Cadastros/PCP/ServicoCipEstrutura';
import { ICipEstrutura, ICipEstruturaCategoria } from '@/models/Entidades/Cadastros/PCP/ICipEstrutura';

export default defineComponent({
  name: 'CipEstruturaModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    Card,
    RequisicaoModal,
    SelecionarSazonalidade,
    SelecionarTipoProducao,
    SelecionarCategoriaPlanoConta,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase,
      obterPermissoes,
      preencherEmpresasComEstrategiaPermissaoDados,
      defineEmpresasSelecionadasCadastroCompartilhado,
      preencherPermissoesDados,
      filtrarPermissaoDadosUsuarioMultiEmpresas,
      apresentarMensagemSucesso,
      apresentarMensagemAlerta,
      verificaConceitoParaApresentarEmpresas,
    } = useTelaBase();
    const {
      modalBase,
      apresentarRetornoRequisicao,
      apresentarBarraProgresso,
      ocultarBarraProgresso,
      defineNovaOperacao,
      defineTextoPadraoBotoes,
      sincronizarRegistro,
    } = useModalBase(props, emit);
    const { gradeBase } = useGradeBase();
    const servicoCipEstrutura = new ServicoCipEstrutura();

    telaBase.identificadorRecurso = 'CADASTRO_CIP_ESTRUTURA';
    telaBase.identificadorPermissao = 'PER_CADASTRO_CIP_ESTRUTURA';

    const state = reactive({
      cipEstrutura: {} as ICipEstrutura,
      editavel: true,
      codigoPlanoContaCategoria: 0,
      nomeCategoriaFinanceira: '',
      categoriaSelecionada: -1,
    });

    function objetoInicial() {
      state.cipEstrutura = {} as ICipEstrutura;
      state.cipEstrutura.codigo = 0;
      state.cipEstrutura.descricao = '';
      state.cipEstrutura.codigoTipoProducao = 0;
      state.cipEstrutura.tipoPeriodo = 1;
      state.cipEstrutura.codigoEmpresa = 0;
      state.cipEstrutura.ativo = true;
      state.cipEstrutura.categorias = [] as ICipEstruturaCategoria[];
      state.editavel = true;
      state.codigoPlanoContaCategoria = 0;
      state.nomeCategoriaFinanceira = '';
      state.categoriaSelecionada = -1;
      telaBase.empresasSelecionadas = [];
    }

    function preencherColunas() {
      gradeBase.colunas = [
        {
          title: 'Codigo', dataIndex: 'codigoPlanoContaCategoria', key: 'CodigoPlanoContaCategoria', position: 0, visible: true, width: 140, align: 'left',
        },
        {
          title: 'Categoria', dataIndex: 'nomeCategoriaFinanceira', key: 'nomeCategoriaFinanceira', position: 1, visible: true,
        },
        {
          title: 'Acoes', key: 'acoes', position: 2, visible: true, fixed: 'right', width: 80, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    function validarCamposObrigatorios(): boolean {
      if (!UtilitarioGeral.valorValido(state.cipEstrutura.descricao) || state.cipEstrutura.descricao.trim() === '') {
        apresentarMensagemAlerta('Informe a descricao.');
        return false;
      }

      if (state.cipEstrutura.descricao.trim().length > 100) {
        apresentarMensagemAlerta('A descricao deve ter no maximo 100 caracteres.');
        return false;
      }

      if (!UtilitarioGeral.validaCodigo(state.cipEstrutura.codigoTipoProducao)) {
        apresentarMensagemAlerta('Informe o tipo de producao.');
        return false;
      }

      if (!UtilitarioGeral.validaCodigo(state.cipEstrutura.tipoPeriodo)) {
        apresentarMensagemAlerta('Informe o tipo de periodo.');
        return false;
      }

      return true;
    }

    async function salvar(salvarNovo: boolean) {
      if (!validarCamposObrigatorios()) {
        return;
      }

      const codigoEmpresaSelecionada = telaBase.empresasSelecionadas.length > 0
        ? telaBase.empresasSelecionadas[0]
        : props.operacao.empresaSelecionada;

      state.cipEstrutura.codigoEmpresa = codigoEmpresaSelecionada;

      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();
      try {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          retorno = await servicoCipEstrutura.incluir(state.cipEstrutura);
        } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          retorno = await servicoCipEstrutura.alterar(state.cipEstrutura);
        }

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
            sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
          } else {
            sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
          }

          apresentarMensagemSucesso(retorno.mensagem);

          if (salvarNovo) {
            objetoInicial();
            telaBase.empresasSelecionadas = [props.operacao.empresaSelecionada];
            defineNovaOperacao(props.operacao);
          } else {
            modalBase.computedVisivel = false;
          }
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      } catch (erro: any) {
        ocultarBarraProgresso();

        const mensagemErro = erro?.response?.data?.mensagem
          ?? erro?.response?.data?.Mensagem
          ?? erro?.message
          ?? 'Ocorreu um erro ao salvar a estrutura CIP.';

        apresentarRetornoRequisicao({
          codigoRegistro: 0,
          status: EStatusRetornoRequisicao.Erro,
          mensagem: mensagemErro,
        });
      }
    }

    function adicionarCategoria() {
      if (!UtilitarioGeral.validaCodigo(state.codigoPlanoContaCategoria)) {
        apresentarMensagemAlerta('Informe a categoria.');
        return;
      }

      const indexExistente = state.cipEstrutura.categorias.findIndex((c) => c.codigoPlanoContaCategoria === state.codigoPlanoContaCategoria);
      if (indexExistente !== -1 && indexExistente !== state.categoriaSelecionada) {
        apresentarMensagemAlerta('Categoria ja adicionada na estrutura CIP.');
        return;
      }

      if (state.categoriaSelecionada > -1) {
        state.cipEstrutura.categorias[state.categoriaSelecionada].codigoPlanoContaCategoria = state.codigoPlanoContaCategoria;
        state.cipEstrutura.categorias[state.categoriaSelecionada].nomeCategoriaFinanceira = state.nomeCategoriaFinanceira;
      } else {
        const categoria = {} as ICipEstruturaCategoria;
        categoria.codigo = 0;
        categoria.codigoCipEstrutura = state.cipEstrutura.codigo;
        categoria.codigoPlanoContaCategoria = state.codigoPlanoContaCategoria;
        categoria.nomeCategoriaFinanceira = state.nomeCategoriaFinanceira;
        state.cipEstrutura.categorias.push(categoria);
      }

      state.codigoPlanoContaCategoria = 0;
      state.nomeCategoriaFinanceira = '';
      state.categoriaSelecionada = -1;
    }

    function editarCategoria(index: number) {
      const categoria = state.cipEstrutura.categorias[index];
      state.codigoPlanoContaCategoria = categoria.codigoPlanoContaCategoria;
      state.nomeCategoriaFinanceira = categoria.nomeCategoriaFinanceira;
      state.categoriaSelecionada = index;
    }

    function excluirCategoria(index: number) {
      state.cipEstrutura.categorias.splice(index, 1);
      state.codigoPlanoContaCategoria = 0;
      state.nomeCategoriaFinanceira = '';
      state.categoriaSelecionada = -1;
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      preencherColunas();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);

        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.cipEstrutura = await servicoCipEstrutura.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          if (!UtilitarioGeral.validaLista(state.cipEstrutura.categorias)) {
            state.cipEstrutura.categorias = [] as ICipEstruturaCategoria[];
          }
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }

        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        verificaConceitoParaApresentarEmpresas();
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      props,
      modalBase,
      state,
      gradeBase,
      EPermissaoDados,
      ECustomRenderRow,
      salvar,
      adicionarCategoria,
      editarCategoria,
      excluirCategoria,
    };
  },
});
